import { createFileRoute } from "@tanstack/react-router";
import type { ClientLocationWithLevel } from "api-types/locations/locationBaseTypes";
import BuilderPortalLayout from "lib/components/builders/BuilderPortalLayout";
import { TaskProvider, useTaskContext } from "lib/components/builders/communities/tasks/TaskContext";
import { TaskLayout } from "lib/components/builders/communities/tasks/TaskLayout";
import TaskStatusSelect from "lib/components/builders/communities/tasks/TaskStatusSelect";
import { getLocationBreadcrumbs } from "lib/components/builders/communities/utils/locationUtils";
import { FetchErrorMessage } from "lib/components/errors/FetchErrorMessage";
import { getBuilderLocationParentsURL } from "lib/endpointHelpers";
import { useFetch } from "lib/fetcher";
import { locationTasksPath, useAppRouterParams, taskPath } from "lib/routeHelpers";
import { useMemo } from "react";
import Loading from "ui/components/Loading";
import withAuthentication from "../../../../../components/withAuthentication";

function TaskPageReady() {
  const { builderId, taskId } = useAppRouterParams();

  if (!builderId || !taskId) {
    return null;
  }

  return (
    <TaskProvider builderId={builderId} taskId={taskId}>
      <TaskPage builderId={builderId} />
    </TaskProvider>
  );
}

function TaskPage({ builderId }: { builderId: string }) {
  const { estimationTask, mutateEstimationTask, loading, error } = useTaskContext();

  const { data: parents } = useFetch<ClientLocationWithLevel[]>(
    estimationTask ? getBuilderLocationParentsURL({ builderId, locationId: estimationTask.locationId }) : "",
    [builderId, estimationTask],
    {
      handleErrorLocally: true,
    },
  );

  const breadcrumbs = useMemo(() => {
    if (!estimationTask) {
      return [];
    }

    return [
      ...getLocationBreadcrumbs(parents, estimationTask.locationId, builderId),
      {
        text: "Jobs",
        href: locationTasksPath(builderId, estimationTask.locationId),
      },
      {
        text: estimationTask.name ?? "",
        href: taskPath(builderId, estimationTask.id),
      },
    ];
  }, [builderId, estimationTask?.id, estimationTask?.locationId, estimationTask?.name, parents]);

  const subheaderItems = estimationTask
    ? [<TaskStatusSelect key="status" estimationTask={estimationTask} onUpdateEstimationTask={mutateEstimationTask} />]
    : undefined;

  return (
    <BuilderPortalLayout
      selected="locations"
      pageTitle={estimationTask?.name ?? "Loading Estimation Job..."}
      header={estimationTask?.name}
      subheaderItems={subheaderItems}
      loadingContent={loading}
      breadcrumbs={breadcrumbs}
      wide
    >
      {loading && <Loading />}
      {error && <FetchErrorMessage fetchError={error} />}
      {estimationTask && <TaskLayout estimationTask={estimationTask} />}
    </BuilderPortalLayout>
  );
}

export const Route = createFileRoute("/builders/$builder_id/tasks/$task_id/")({
  component: withAuthentication(TaskPageReady, { features: ["estimationTasks"] }),
});
